import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonDropdown, Icon, Typography } from "@/components/atoms";
import { BulkBookingClient } from "@/components/organisms/BulkBooking/BulkBookingContext";
import { BulkBookingModal } from "@/components/organisms/BulkBooking/BulkBookingModal";
import { BulkUploadMenuSection } from "@/components/organisms/BulkBooking/BulkUploadMenuSection";
import { ClientPickerModal, ClientPickerProfile } from "@/components/organisms/ClientPickerModal/ClientPickerModal";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { clearBooking, setBookingPassenger, setStartNewGuest } from "@/redux/slices/clientBooking/clientBookingSlice";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { getAssetUrl } from "@/utils";
import { useCanManageAsAssistant } from "./ClientNavLinks";

interface ClientBookTripButtonProps {
  /** "header" = desktop nav (sm button); "drawer" = full-width entry in the mobile drawer footer (lg button). */
  variant?: "header" | "drawer";
}

/**
 * The client portal's "Book a Trip" nav control — a split button (DEV-1215): the left half books
 * directly, the caret opens the alternatives plus the bulk-upload options. Styled from the same
 * caret theme as the Client Manager Actions button, so the two look identical.
 *
 * Plain clients book for themselves. Assistant accounts (EA / Travel Manager, DEV-1296) also get
 * "Book for a client or guest", which opens one picker over their managed clients AND their own
 * saved guests (DEV-1593): a client routes to the `book-for/:accessId` scoped sheet, a guest is
 * seeded as the passenger on a booking under the EA's own account.
 */
export const ClientBookTripButton = ({ variant = "header" }: ClientBookTripButtonProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canManage = useCanManageAsAssistant();
  const accountProfile = useAppSelector(profileSelector);
  // "book" navigates to the scoped sheet; "bulk" opens the wizard for that client. Undefined = closed.
  const [picking, setPicking] = useState<"book" | "bulk">();
  const [bulkClient, setBulkClient] = useState<BulkBookingClient>();
  // A plain client bulk-uploads for themselves, so there's no client to pick.
  const [selfBulkOpen, setSelfBulkOpen] = useState(false);

  const size = variant === "drawer" ? "lg" : "sm";
  const widthClassName = variant === "drawer" ? "w-full" : undefined;
  // The drawer button sits in the sheet's bottom footer, so its menu must open upward, not down.
  const menuPosition = variant === "drawer" ? "top-end" : "bottom-end";

  // Self-service entry — reset any in-flight draft first (as the managed-client path does) so a previous
  // (possibly abandoned) attempt's locations/passenger don't carry into a fresh booking.
  const bookForSelf = () => {
    dispatch(clearBooking());
    navigate("/client/booking");
  };

  // The EA's own row, pinned first in the picker so booking for themselves doesn't mean backing out
  // of it into the menu's "Book for yourself".
  const selfName =
    [accountProfile?.firstName, accountProfile?.lastName].filter(Boolean).join(" ") || accountProfile?.displayName || accountProfile?.email;
  const selfProfile: ClientPickerProfile = {
    id: "self",
    name: selfName ? `${selfName} (You)` : "You",
    // profile.avatar is a relative CDN key, not a URL (as HeaderProfile resolves it) — passed raw it
    // 404s against the app origin AND suppresses the initials fallback, leaving a broken glyph.
    avatarSrc: accountProfile?.avatar ? getAssetUrl(accountProfile.avatar, { height: 120, width: 120, fit: "cover" }) : null,
    kind: "self",
  };

  // "New Guest" CTA (DEV-1610): a guest booking stays on the EA's own account (they hold the card),
  // so — like the guest-row path — reset the draft and go to /client/booking, but flag the draft so
  // the sheet lands straight on the passenger new-guest form once the route is in.
  const bookNewGuest = () => {
    dispatch(clearBooking());
    dispatch(setStartNewGuest(true));
    setPicking(undefined);
    navigate("/client/booking");
  };

  // Picking a managed client resets any in-flight draft (as the operator flow does) and routes to
  // the assistant-scoped booking sheet keyed on the access-link id.
  const handleSelect = (accessId: string, profile?: ClientPickerProfile) => {
    const purpose = picking;
    setPicking(undefined);
    if (!profile) return;

    if (purpose === "bulk") {
      // For an assistant the picker's id is the access-link id, so the import scopes on the
      // profile's `accountId` (the client's account uuid) instead.
      if (!profile.accountId) return;
      setBulkClient({ accountId: profile.accountId, name: profile.name, avatarSrc: profile.avatarSrc });
      return;
    }

    if (profile.kind === "self") {
      bookForSelf();
      return;
    }

    // A guest is one of the EA's own favourite passengers, so the booking stays on the EA's account
    // (they hold the card) with the guest preselected as the passenger.
    if (profile.kind === "guest") {
      // Fail closed: without the favourite there is nothing to seed, and falling through would open
      // the assistant-scoped sheet on a favourite's uuid.
      if (!profile.guest) return;
      const { uuid, title, first_name, last_name, phone, email } = profile.guest;
      dispatch(clearBooking());
      dispatch(setBookingPassenger({ id: uuid, title, firstName: first_name, lastName: last_name, phone, email, kind: "favourite" }));
      navigate("/client/booking");
      return;
    }

    dispatch(clearBooking());
    navigate(`/client/book-for/${accessId}`);
  };

  // An assistant has no trips of their own by default, so their primary action is the client picker;
  // a plain client goes straight into self-service.
  const primaryAction = canManage ? () => setPicking("book") : bookForSelf;

  return (
    <>
      <ButtonDropdown
        startIcon="Add"
        size={size}
        position={menuPosition}
        className={widthClassName}
        menuClassName="w-[298px]"
        onClick={primaryAction}
      >
        <>Book a Trip</>
        <>
          {canManage ? (
            <>
              <ButtonDropdown.Item onClick={() => setPicking("book")} className="w-[298px]">
                <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
                <Typography className="text-neutral-black">Book for a client or guest</Typography>
              </ButtonDropdown.Item>
              <ButtonDropdown.Item onClick={bookForSelf} className="w-[298px]">
                <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
                <Typography className="text-neutral-black">Book for yourself</Typography>
              </ButtonDropdown.Item>
            </>
          ) : (
            <ButtonDropdown.Item onClick={bookForSelf} className="w-[298px]">
              <Icon name="Add" className="mr-2 text-neutral-black" size="md" />
              <Typography className="text-neutral-black">Book a Trip</Typography>
            </ButtonDropdown.Item>
          )}
          {/*
            An assistant gets both, exactly as the Book a Trip items above do. They have clients to
            book for AND trips of their own, and offering only the client picker left them unable to
            bulk upload for themselves — something the booking sheet has always let them do one menu
            item higher. An assistant managing nobody had it worse: the picker was their only way in,
            and it opens empty.
          */}
          <BulkUploadMenuSection
            onUpload={() => (canManage ? setPicking("bulk") : setSelfBulkOpen(true))}
            onUploadForSelf={canManage ? () => setSelfBulkOpen(true) : undefined}
          />
        </>
      </ButtonDropdown>

      {canManage && (
        <ClientPickerModal
          source="assistant"
          includeGuests={picking === "book"}
          pinnedProfiles={picking === "book" ? [selfProfile] : undefined}
          onNewGuest={picking === "book" ? bookNewGuest : undefined}
          open={!!picking}
          onClose={() => setPicking(undefined)}
          onSelect={handleSelect}
          title={picking === "bulk" ? "Select a Profile to Upload a Bulk Booking" : undefined}
          description={picking === "bulk" ? "Choose a client to upload bulk booking" : undefined}
        />
      )}

      {/*
        One wizard, two ways in: with a picked client, or for the signed-in account. An assistant can
        reach either — hence both flags here rather than a branch on `canManage`, which used to make
        `selfBulkOpen` dead for them no matter what opened it.

        `client` is left undefined on the self path, which is what scopes the import to the account
        doing it — the same undefined a plain client has always sent.
      */}
      <BulkBookingModal
        open={!!bulkClient || selfBulkOpen}
        onClose={() => {
          setBulkClient(undefined);
          setSelfBulkOpen(false);
        }}
        client={bulkClient}
      />
    </>
  );
};
